import React, { useState } from "react";
import styles from "./DeliveryList.module.scss";

import moment from "moment";
import { DeliveryProps } from "../../types/orders";
import { useAppDispatch } from "../../app/hooks";
import {
  setEditDelivery,
  toggleDeliveryModal,
} from "../../features/orders/ordersSlice";
import { FaFileInvoiceDollar } from "react-icons/fa6";
import useConfirmPopup from "../../context/useConfirmPopup";

type Props = {
  deliveries: DeliveryProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteDelivery: (id: number, password: string) => void;
  handleExportPaymentPdf: (paymentId: number) => void;
};

const DeliveryList = ({
  deliveries,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteDelivery,
  handleExportPaymentPdf,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditDelivery = (delivery: DeliveryProps) => {
    dispatch(setEditDelivery(delivery));
    dispatch(toggleDeliveryModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const response = await confirmDelete(
      `You will not be able to revert this!`,
      `Are you sure?`,
      "Yes, delete it!",
      true
    );

    if (response.confirmed) {
      setIsActionLoading(id);
      await handleDeleteDelivery(id, response.password);
      setIsActionLoading(0);
    }
  };

  return (
    <div className={styles.DeliveryList}>
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Sr. No.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Order ID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    PID
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Party
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Item
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Process
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Qty
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Total Rs.
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-base text-primary-1 font-bold uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={10}
                      className="text-primary-2 font-bold text-xl text-center py-2"
                    >
                      Please wait...
                    </td>
                  </tr>
                ) : (
                  <>
                    {deliveries.length > 0 ? (
                      <>
                        {deliveries.map((delivery, index) => (
                          <tr key={index}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(pageNumber - 1) * pageSize + index + 1}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.order_id || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery.payment_id || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery?.order?.party?.firm_name || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery?.order?.name ? `${delivery.order.name} - ` : ""}
                              {delivery?.order?.item?.name || "-"}{" "}
                              {delivery?.order?.inch > 1 && ` ${delivery.order.inch} Inch`}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery?.order?.process?.name || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {delivery?.qty || "-"}
                            </td>
                            <td className="text-right px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {(
                                (delivery?.qty || 0) *
                                (delivery?.order?.rate || 0) *
                                (delivery?.order?.inch || 1)
                              ).toFixed(2) || "-"}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {moment(delivery.created_at).format(
                                "DD-MM-YYYY hh:mm A"
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              {isActionLoading === delivery.id ? (
                                <p className="text-primary-2 font-bold text-right text-lg">
                                  Please wait...
                                </p>
                              ) : (
                                <div className="flex items-center justify-end gap-4">
                                  <button
                                    onClick={() => handleEditDelivery(delivery)}
                                    className="text-indigo-600 hover:text-indigo-900"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteRow(delivery.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    Delete
                                  </button>
                                  {delivery.payment_id && (
                                    <button
                                      onClick={() =>
                                        handleExportPaymentPdf(delivery.payment_id!)
                                      }
                                    >
                                      <FaFileInvoiceDollar className="text-xl text-primary-1" />
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={10}
                          className="text-primary-2 font-bold text-xl text-center py-2"
                        >
                          No delivery details found
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
export default DeliveryList;
